import { Directive, ElementRef, EventEmitter, OnDestroy, Output } from '@angular/core';

/**
 * Directive that emits an event when focus moves completely outside the element and its children.
 * 
 * This is different from regular blur which triggers even when focus moves to a child element.
 * DeepBlurDirective solves a common UX problem where you need to detect when a user has completely 
 * moved away from a component (including all of its children), rather than just moving focus between
 * elements within the component.
 * 
 * @example
 * ```html
 * <div deep-blur (deep-blur)="handleDeepBlur()">
 *   <input type="text" />
 *   <button>Submit</button>
 * </div>
 * ```
 * 
 * @example
 * ```typescript
 * @Component({...})
 * export class MyComponent {
 *   handleDeepBlur() {
 *     // This will only fire when focus moves completely outside the div and its children
 *     console.log('User has moved focus away from the entire component');
 *   }
 * }
 * ```
 */
@Directive({
  selector: '[deep-blur]',
  standalone: true,
})
export class DeepBlurDirective implements OnDestroy {
  /**
   * Event emitted when focus moves completely outside the element and its children.
   * Unlike standard blur events, this only fires when focus truly leaves the component.
   */
  @Output('deep-blur') value = new EventEmitter<void>();
  
  /** @internal Cleanup function to remove event listeners on destroy. */
  private cleanupFn: (() => void) | null = null;

  /**
   * Creates an instance of DeepBlurDirective.
   * Sets up event listeners on the host element to detect deep blur events.
   * @param ui Reference to the host DOM element.
   */
  constructor(ui: ElementRef<HTMLElement>) {
    const dom = ui.nativeElement;
    
    const onBlur = (e: FocusEvent) => {
      // Get the element receiving focus after blur
      // e.relatedTarget works in modern browsers
      // document.activeElement is fallback for older browsers
      const targetElement =
        (e.relatedTarget as HTMLElement) || document.activeElement as HTMLElement;

      if (!this.containsDom(dom, targetElement)) {
        // Small delay to ensure the DOM has settled
        setTimeout(() => {
          this.value.emit();
        }, 10);
      }
    };

    // All modern browsers support addEventListener
    dom.addEventListener('blur', onBlur, true);
    
    // Store cleanup function for OnDestroy
    this.cleanupFn = () => {
      dom.removeEventListener('blur', onBlur, true);
    };
  }

  /**
   * Checks if a DOM node is contained within another node.
   * @internal
   * @param parent The potential parent node.
   * @param child The node to check.
   * @returns True if child is contained within parent.
   */
  private containsDom(parent: Node, child: Node | null): boolean {
    if (!child) return false;
    
    let current: Node | null = child;
    while (current) {
      if (current === parent) {
        return true;
      }

      current = current.parentNode;
    }

    return false;
  }

  /**
   * Lifecycle hook that is called when the directive is destroyed.
   * Removes all event listeners to prevent memory leaks.
   */
  ngOnDestroy(): void {
    if (this.cleanupFn) {
      this.cleanupFn();
      this.cleanupFn = null;
    }
  }
}
